/**
 * What a new chart is.
 *
 * One definition, used by both the demo workspace and the layout templates —
 * because a chart the templates create has to be the same thing as a chart the
 * demo ships, or "2 across" would produce a second chart that renders nothing
 * while looking correct.
 *
 * Every id is minted per call. Two groups sharing a pane or axis id would
 * collide in the render plan rather than fail loudly, so a *factory* is the
 * shape this has to take — a module constant spread into two layouts is the bug
 * this prevents.
 *
 * The aliases are the contract with the data feed: `chart-feed` requests one
 * indicator per non-OHLC `outputAlias` and keys the response back by the same
 * name. A chart built with different aliases is a chart with no data.
 */

import { paneWeights, stackFractions } from "@qufolio/chart-engine";
import {
  mintUlid,
  type ChartGroup,
  type Pane,
  type Plot,
  type PlotType,
  type Section,
} from "@qufolio/model";

/** Output aliases the plots subscribe to (chart-feed maps transport columns onto these). */
export const CHART_ALIASES = {
  ohlc: "ohlc",
  /**
   * The Heikin-Ashi candle's alias — `haOhlc` expands to `haOpen`…`haClose`.
   *
   * A separate alias rather than a flag on the plot, because the feed decides
   * which columns a candle reads *from its alias*: `chart-feed`'s
   * `derivedCandleColumns` is what asks the server for the four HA columns and
   * matches them back. A candlestick still aliased `ohlc` on an HA chart would
   * be handed the base frame's real prices and draw ordinary candles.
   */
  haOhlc: "haOhlc",
  ema20: "ema_20",
  ema50: "ema_50",
  volume: "volume",
} as const;

/**
 * What the chart-type control shows, which is **two contract fields wearing one
 * hat**.
 *
 * Candlestick / OHLC / Line / Mountain are four renderables over the same bars
 * and live on `Plot.type`. Heikin-Ashi is not a renderable at all — it re-forms
 * every bar and lives on `ChartGroup.barSource`. They are one menu because that
 * is how the choice is actually made: a person picking how the price looks does
 * not care which field carries it, and every charting app they already use puts
 * Heikin-Ashi in this list.
 *
 * Exclusive, therefore: picking Line turns Heikin-Ashi off. Drawing HA candles
 * *and* a real close line together is two plots, which the feed supports and
 * this control deliberately does not express.
 */
export type ChartTypeChoice = PlotType | "heikinAshi";

/**
 * The slice of the price pane an overlaid volume series draws into.
 *
 * A fifth, because that is where the eye already expects volume — the same
 * proportion the volume pane takes at the default `2fr`/`1fr` split, so turning
 * the overlay on does not change how big the bars look, only what they sit
 * under.
 */
export const VOLUME_BAND = { from: 0, to: 0.2 } as const;

/**
 * Whether volume is drawn **on** the price pane rather than beside it.
 *
 * Derived rather than stored, for `matchTemplate`'s reason: a flag would be a
 * second source of truth that the first pane edit falsifies. What makes it true
 * is a volume plot living on the price pane, which is a fact about the panes.
 */
export function volumeOverlaid(group: ChartGroup): boolean {
  const price = group.panes.find((p) => p.role === "price");
  return (price?.plots ?? []).some((p) => p.outputAlias === CHART_ALIASES.volume);
}

/** The control's current value — the bar source if it is set, else the plot's type. */
export function chartTypeChoice(group: ChartGroup): ChartTypeChoice | undefined {
  if (group.barSource === "heikinAshi") return "heikinAshi";
  return pricePlot(group)?.type;
}

/**
 * The volume pane a new chart opens with — and what Indicators › Volume puts
 * back once it has been removed (owner, 2026-10-19), so the two cannot drift.
 */
export function newVolumePane(order: number, axisId: string = mintUlid()): Pane {
  return {
    id: mintUlid(),
    role: "volume",
    order,
    height: "1fr",
    axes: [
      {
        id: axisId,
        side: "right",
        autoRange: "always",
        scale: "linear",
        format: "0,0",
        grid: { minorsPerMajor: 2 },
      },
    ],
    plots: [
      {
        id: mintUlid(),
        order: 0,
        type: "column",
        indicatorRef: "VOL",
        outputAlias: CHART_ALIASES.volume,
        axisRef: axisId,
        style: { opacity: 0.8 },
        palette: { upColor: "positive", downColor: "negative", colorMode: "delta" },
      },
    ],
    annotations: [],
  };
}

/** A price pane (candles + two EMAs) over a volume pane. */
export function newChartGroup(symbol: string, frequency = "1d"): ChartGroup {
  const priceAxisId = mintUlid();
  const volumeAxisId = mintUlid();

  const pricePane: Pane = {
    id: mintUlid(),
    role: "price",
    order: 0,
    height: "3fr",
    axes: [
      {
        id: priceAxisId,
        side: "right",
        autoRange: "always",
        scale: "linear",
        format: "0.00",
        grid: { minorsPerMajor: 2 },
      },
    ],
    plots: [
      {
        id: mintUlid(),
        order: 0,
        type: "candlestick",
        indicatorRef: "OHLC",
        outputAlias: CHART_ALIASES.ohlc,
        axisRef: priceAxisId,
        palette: { upColor: "positive", downColor: "negative", colorMode: "sign" },
      },
      {
        id: mintUlid(),
        order: 1,
        type: "line",
        indicatorRef: "EMA",
        outputAlias: CHART_ALIASES.ema20,
        axisRef: priceAxisId,
        style: { stroke: "accent.blue", thickness: 1.5 },
      },
      {
        id: mintUlid(),
        order: 2,
        type: "line",
        indicatorRef: "EMA",
        outputAlias: CHART_ALIASES.ema50,
        axisRef: priceAxisId,
        style: { stroke: "accent.orange", thickness: 1.5 },
      },
    ],
    annotations: [],
  };

  const volumePane = newVolumePane(1, volumeAxisId);

  return {
    id: mintUlid(),
    symbol,
    frequency,
    // What to **load**, not what to show. The server honours this now (it used
    // to ignore it and send 500 to everyone), and a chart showing ~150 bars
    // still wants a few thousand behind it to pan through without a refetch.
    barsRange: 1500,
    xAxis: { type: "category" },
    panes: [pricePane, volumePane],
  };
}

/**
 * A chart section at the origin — the template that places it owns its cell.
 *
 * `symbol` defaults to the symbol of the chart it is being added beside, which
 * the caller supplies: a new pane showing whatever you were already looking at
 * is a better starting point than a hardcoded ticker, and changing it is one
 * click.
 */
export function newChartSection(symbol = "AAPL", frequency = "1d"): Section {
  return {
    id: mintUlid(),
    kind: "chartGroup",
    row: 0,
    column: 0,
    rowSpan: 1,
    columnSpan: 1,
    config: newChartGroup(symbol, frequency),
  };
}

/**
 * The plot the chart-type control drives: the price pane's OHLC-fed series.
 *
 * Falls back to the pane's first plot, because a price pane always has one and a
 * toolbar bound to nothing silently stops working. Exported rather than repeated
 * because two callers need the *same* answer — the toolbar that changes it, and
 * the sync fan-out that has to find the matching plot on every other chart. Two
 * copies of this rule would sync the wrong plot the moment one of them changed.
 */
export function pricePlot(group: ChartGroup): Plot | undefined {
  const pane = group.panes.find((p) => p.role === "price");
  return (
    pane?.plots?.find((p) => p.type === "candlestick" || p.type === "ohlc") ??
    pane?.plots?.[0]
  );
}


/**
 * Where the price pane's bottom edge sits, as a fraction of the chart's height.
 *
 * What the controls strip hangs off. It comes from `stackFractions` — the same
 * function the renderer positions SubCharts with and `PaneDividers` derives its
 * seams from — because anything pinned to a pane edge that computes its own
 * arithmetic drifts off that edge the first time a divider is dragged.
 *
 * The mistake this replaced was simpler and quieter: the strip was positioned
 * against the *container*, which spans the whole stack, so it sat in the last
 * pane rather than the price one. On a single-pane chart the two are the same
 * number, which is why it looked correct.
 *
 * `1` when there is no `price` pane — a chart that is one indicator has nothing
 * to sit above, and the container's own bottom is then the right answer rather
 * than a special case to handle at the call site.
 */
/**
 * Where the price pane's **top** edge sits, as a fraction of the chart's height.
 *
 * Its twin above, and the same mistake waiting: anything pinned to the top of
 * the price pane and measured from the *container* is only right while the
 * price pane is pane 0. It stops being pane 0 the first time somebody presses
 * the reorder strip's ⌃ on the pane below it — and the watermark then drew over
 * whatever pane had been moved to the top.
 *
 * `0` when there is no `price` pane, for `priceBottomFraction`'s reason.
 */
export function priceTopFraction(panes: readonly Pane[]): number {
  const bands = stackFractions(paneWeights(panes));
  const index = panes.findIndex((pane) => pane.role === "price");
  return (index >= 0 ? bands[index]?.y : 0) ?? 0;
}

export function priceBottomFraction(panes: readonly Pane[]): number {
  const bands = stackFractions(paneWeights(panes));
  const index = panes.findIndex((pane) => pane.role === "price");
  const band = index >= 0 ? bands[index] : undefined;
  return band ? band.y + band.height : 1;
}
